import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Tag,
  BarChart2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Shield,
  TrendingUp,
  Clock,
  Info,
  CheckCircle,
  XCircle,
  Loader,
  Loader2,
  Briefcase,
  Award,
  Bookmark, // Added for watchlist icon
} from "lucide-react";
import mutualFundService from "@/services/api/mutual-fund.service";
import TransactionService from "@/services/api/transaction.service";
import { useNavigation } from "@/contexts/notification/NavigationContext";

// --- Helper Function ---
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null || isNaN(num)) return "N/A";
  const number = parseFloat(num);
  if (isNaN(number)) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(number);
};

// --- Main Component ---
export default function SchemeDetailsPage() {
  const { schemeCode } = useParams(); // This schemeCode is what we need to send
  const navigate = useNavigate();
  const { setPendingPurchase } = useNavigation();
  // --- Component State ---
  const [isLoading, setIsLoading] = useState(true);
  const [scheme, setScheme] = useState(null);
  const [returns, setReturns] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [investAmount, setInvestAmount] = useState(1000);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSection, setExpandedSection] = useState("returns");
  const [isPurchasing, setPurchasing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [isWalletLoading, setIsWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);
  const [isAddingToWatchlist, setIsAddingToWatchlist] = useState(false);

  // --- Memoized Calculations ---
  const latestNAV = useMemo(() => {
    const navValue = parseFloat(scheme?.data?.[0]?.nav);
    return isNaN(navValue) ? null : navValue;
  }, [scheme?.data]);

  const latestNAVDate = useMemo(
    () => scheme?.data?.[0]?.date || null,
    [scheme?.data]
  );

  const estimatedUnits = useMemo(() => {
    const amount = parseFloat(investAmount);
    if (latestNAV === null || isNaN(amount) || amount <= 0 || latestNAV <= 0)
      return "0.000";
    return formatNumber(amount / latestNAV, 3);
  }, [investAmount, latestNAV]);

  const riskProfile = useMemo(() => {
    const category = scheme?.meta?.scheme_category?.toLowerCase() || "";
    if (
      category.includes("equity") ||
      category.includes("small cap") ||
      category.includes("mid cap") ||
      category.includes("flexi cap") ||
      category.includes("sectoral") ||
      category.includes("thematic")
    )
      return {
        level: "High",
        description:
          "Higher potential returns, higher volatility. Suited for long-term goals & higher risk tolerance.",
      };
    if (
      category.includes("debt") ||
      category.includes("banking") ||
      category.includes("liquid") ||
      category.includes("gilt") ||
      category.includes("corporate bond") ||
      category.includes("money market")
    )
      return {
        level: "Low",
        description:
          "Focuses on capital preservation with stable returns. Ideal for conservative investors or short-term goals.",
      };
    if (
      category.includes("hybrid") ||
      category.includes("balanced") ||
      category.includes("multi asset") ||
      category.includes("dynamic asset")
    )
      return {
        level: "Moderate",
        description:
          "Balances risk and return with a mix of assets. Suitable for medium-term outlook.",
      };
    return {
      level: "Moderate",
      description: "Risk level varies. Review fund details carefully.",
    };
  }, [scheme?.meta?.scheme_category]);

  const inceptionGrowth = useMemo(() => {
    if (!scheme?.data || scheme.data.length < 2) return null;
    const firstNav = parseFloat(scheme.data[scheme.data.length - 1].nav);
    const currentNav = parseFloat(scheme.data[0].nav);
    if (isNaN(firstNav) || isNaN(currentNav) || firstNav === 0) return null;
    return ((currentNav - firstNav) / firstNav) * 100;
  }, [scheme?.data]);

  const growthFromInceptionFormatted = useMemo(() => {
    return inceptionGrowth !== null
      ? `${formatNumber(inceptionGrowth)}%`
      : "N/A";
  }, [inceptionGrowth]);

  const insufficientBalance = useMemo(() => {
    if (walletBalance === null || isWalletLoading || walletError) return false;
    const amount = parseFloat(investAmount);
    if (isNaN(amount) || amount <= 0) return false;
    return amount > walletBalance;
  }, [investAmount, walletBalance, isWalletLoading, walletError]);

  // --- Data Fetching Functions ---
  const showNotification = useCallback((message, type = "info") => {
    setNotification({ message, type });
    const timer = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(timer);
  }, []);

  const fetchSchemeData = useCallback(async () => {
    if (!schemeCode) return;

    setIsLoading(true);
    setError(null);
    setReturns(null);

    try {
      const response = await mutualFundService.getSchemeDetails(schemeCode);

      if (!response?.data?.success || !response?.data?.data?.data) {
        throw new Error(
          response?.data?.message || "Scheme details unavailable."
        );
      }
      setScheme(response.data.data.data);

      const returnsPeriods = ["1y", "3y", "5y"];
      const returnsPromises = returnsPeriods.map((period) =>
        mutualFundService
          .calculateReturns(schemeCode, { period })
          .catch((err) => {
            return { data: { success: false } };
          })
      );
      const returnsResponses = await Promise.all(returnsPromises);

      const returnsData = returnsPeriods.reduce((acc, period, index) => {
        acc[period] = returnsResponses[index]?.data?.success
          ? returnsResponses[index].data.data
          : null;
        return acc;
      }, {});
      setReturns(returnsData);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load scheme details.";
      setError(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [schemeCode]);

  const fetchWalletBalance = useCallback(async () => {
    setIsWalletLoading(true);
    setWalletError(null);
    try {
      const response = await TransactionService.getWalletBalance();

      if (response.success && response.data) {
        const balance = response.data.balance ?? 0;
        setWalletBalance(parseFloat(balance));
      } else {
        if (response.error === "Wallet not found" || response.status === 404) {
          setWalletError("Wallet not found. Create one if needed.");
          setWalletBalance(0);
        } else {
          throw new Error(response.error || "Failed to load wallet balance.");
        }
      }
    } catch (err) {
      setWalletError(err.message);
      setWalletBalance(0);
    } finally {
      setIsWalletLoading(false);
    }
  }, []);

  // --- Initial Data Fetch Effect ---
  useEffect(() => {
    fetchSchemeData();
    fetchWalletBalance();
  }, [fetchSchemeData, fetchWalletBalance]);

  // --- Handlers ---
  const handlePurchase = useCallback(async () => {
    const amount = parseFloat(investAmount);
    if (!scheme || isNaN(amount) || amount < 100) {
      showNotification("Enter valid amount (min ₹100).", "warning");
      return;
    }
    if (walletBalance === null) {
      showNotification("Wallet balance loading, please wait.", "info");
      return;
    }
    if (walletError) {
      showNotification(`Wallet error: ${walletError}`, "error");
      return;
    }
    if (isPurchasing) return;

    setPurchasing(true);
    setError(null);

    try {
      if (!schemeCode || isNaN(amount))
        throw new Error("Invalid data for purchase.");


      const response = await mutualFundService.purchaseMutualFund(
        schemeCode,
        amount
      );


      if (response?.data?.success) {
        showNotification("Investment successful! Redirecting...", "success");
        fetchWalletBalance();
        setTimeout(() => navigate("/portfolio"), 2500);
      } else {
        throw new Error(
          response?.data?.message || "Investment failed. Please check details."
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Investment failed. Please try again.";
      showNotification(errorMessage, "error");
    } finally {
      setPurchasing(false);
    }
  }, [
    scheme,
    investAmount,
    schemeCode,
    navigate,
    insufficientBalance,
    walletBalance,
    walletError,
    isPurchasing,
    showNotification,
    fetchWalletBalance,
  ]);

  const handleAddToWatchlist = useCallback(async () => {
    if (!schemeCode) {
      showNotification("No scheme code available.", "error");
      return;
    }
    if (isAddingToWatchlist) {
      return;
    }
    setIsAddingToWatchlist(true);

    try {
      const payload = { schemeCode: schemeCode };
      const response = await mutualFundService.addMutualFundToWatchlist(payload);

      // Check for success
      if (response?.data?.success || response?.success) {
        showNotification(
          response?.data?.message || "Successfully added to watchlist!",
          "success"
        );
      } else {
        throw new Error(
          response?.data?.message ||
          response?.error ||
          "Failed to add to watchlist."
        );
      }
    } catch (error) {

      let errorMessage = "Failed to add to watchlist. Please try again.";

      // Handle the specific nested error structure you showed
      if (error?.response?.data) {
        const errorData = error.response.data;

        // First check if there's a nested error object with message
        if (errorData.error && errorData.error.message) {
          errorMessage = errorData.error.message;
        }
        // Check if there's a direct message property
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Check if there's a direct error property (string)
        else if (errorData.error && typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
        // If errorData is a string, use it directly
        else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        // Try other common error properties
        else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (errorData.details) {
          errorMessage = errorData.details;
        } else if (errorData.description) {
          errorMessage = errorData.description;
        }
      }
      // Fallback to error.message if response.data doesn't have the info
      else if (error?.message && error.message !== "Request failed with status code 400") {
        errorMessage = error.message;
      }

      // Check if the error indicates the item is already in watchlist
      const lowerErrorMsg = errorMessage.toLowerCase();
      if (lowerErrorMsg.includes("already exists") ||
        lowerErrorMsg.includes("already") ||
        lowerErrorMsg.includes("duplicate") ||
        lowerErrorMsg.includes("exists in watchlist")) {
        showNotification(errorMessage, "warning");
      } else {
        showNotification(errorMessage, "error");
      }
    } finally {
      setIsAddingToWatchlist(false);
    }
  }, [schemeCode, showNotification, isAddingToWatchlist]);

  const toggleSection = useCallback((section) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  }, []);

  const getReturnPercentage = useCallback(
    (period) => {
      if (!returns || !returns[period]) return null;

      let returnObj = returns[period];


      if (returnObj?.data?.data?.absoluteReturn !== undefined) {
        return parseFloat(returnObj.data.data.absoluteReturn);
      }

      let returnValue = returnObj?.percentage;
      if (returnValue === undefined || returnValue === null) {
        returnValue = returnObj?.absoluteReturn;
      }
      if (returnValue === undefined || returnValue === null) {
        returnValue = returnObj?.data?.absoluteReturn;
      }
      if (
        returnValue === undefined ||
        (returnValue === null && typeof returnObj === "number")
      ) {
        returnValue = returnObj;
      }

      if (
        returnValue === undefined ||
        returnValue === null ||
        isNaN(returnValue)
      ) {
        return null;
      }

      return parseFloat(returnValue);
    },
    [returns]
  );

  // --- Render Components ---
  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <div className="flex flex-col items-center text-center">
          <Loader2 className="animate-spin h-12 w-12 text-blue-600 mb-3" />
          <p className="text-gray-600 font-medium">Loading Fund Details...</p>
        </div>
      </div>
    );
  }

  if (error && !scheme) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="container mx-auto max-w-2xl">
          <ErrorDisplay message={error} />
          <button
            onClick={() => navigate("/mutual-funds")}
            className="mt-4 flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Mutual Funds
          </button>
        </div>
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
        <p>Could not load scheme data.</p>
      </div>
    );
  }

  const isInvestButtonDisabled =
    isLoading ||
    isPurchasing ||
    investAmount < 100;

  // --- Final Render ---
  return (
    <div className="min-h-screen sticky mt-10 bg-gradient-to-b from-gray-50 via-gray-50 to-gray-100 font-sans">
      {notification && (
        <div className="fixed top-24 right-6 z-50 animate-slide-in">
          <Notification message={notification.message} type={notification.type} />
        </div>
      )}

      <header className="bg-white shadow-sm sticky top-20 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => (window.location.href = "/mutual-funds")}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2 p-1 -ml-1"
          >
            <ArrowLeft size={18} className="mr-1" /> Back
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 break-words pr-8 leading-tight">
            {scheme.meta.scheme_name}
          </h1>
          <div className="flex flex-wrap items-center mt-2 text-xs sm:text-sm text-gray-500 gap-x-4 gap-y-1">
            <div className="flex items-center">
              <Tag size={14} className="mr-1.5 text-gray-400" />
              <span>{scheme.meta.fund_house}</span>
            </div>
            <div className="flex items-center">
              <BarChart2 size={14} className="mr-1.5 text-gray-400" />
              <span>{scheme.meta.scheme_category}</span>
            </div>
            <div className="flex items-center">
              <span className="font-medium mr-1">Type:</span>
              <span>{scheme.meta.scheme_type}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && scheme && <ErrorDisplay message={error} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <section className="bg-white rounded-lg shadow-md border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <TrendingUp className="mr-2 text-blue-600" size={20} /> Overview
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <InfoCard
                  label="Current NAV"
                  value={latestNAV}
                  date={latestNAVDate}
                  isCurrency
                />
                <InfoCard
                  label="Since Inception"
                  value={inceptionGrowth}
                  suffix="%"
                  date={scheme.data?.[scheme.data.length - 1]?.date}
                  datePrefix="Started"
                  isPercentage
                />
                <InfoCard
                  label="Risk Level"
                  value={riskProfile.level}
                  icon={Shield}
                  iconColor={
                    riskProfile.level === "High"
                      ? "text-red-500"
                      : riskProfile.level === "Low"
                        ? "text-green-500"
                        : "text-yellow-500"
                  }
                  description={`${riskProfile.level} volatility`}
                />
              </div>
            </section>

            <div className="bg-white rounded-lg shadow-md border border-gray-100 divide-y divide-gray-200">
              <AccordionSection
                title="Historical Returns"
                sectionId="returns"
                Icon={TrendingUp}
                expandedSection={expandedSection}
                toggleSection={toggleSection}
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {["1y", "3y", "5y"].map((period) => {
                    const perf = getReturnPercentage(period);
                    return (
                      <InfoCard
                        key={period}
                        label={`${period.replace("y", "")} Year`}
                        value={perf}
                        suffix="%"
                        isPercentage
                        isCompact
                      />
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-4 text-center">
                  Past performance is not indicative of future returns.
                </p>
              </AccordionSection>
              <AccordionSection
                title="Risk Profile Details"
                sectionId="risk"
                Icon={Shield}
                expandedSection={expandedSection}
                toggleSection={toggleSection}
              >
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-3">
                    <Shield
                      className={`mr-2 ${riskProfile.level === "High"
                        ? "text-red-500"
                        : riskProfile.level === "Low"
                          ? "text-green-500"
                          : "text-yellow-500"
                        }`}
                      size={20}
                    />
                    <h3 className="font-semibold text-gray-800">
                      Risk Level: {riskProfile.level}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    {riskProfile.description}
                  </p>
                  <div className="mt-4 bg-white p-3 rounded border border-gray-200">
                    <div className="flex items-center mb-2">
                      <Info size={16} className="text-blue-500 mr-2" />
                      <h4 className="font-medium text-sm text-gray-800">
                        Investor Suitability
                      </h4>
                    </div>
                    <p className="text-xs text-gray-600">
                      {riskProfile.level === "High"
                        ? "Best for long-term horizon (5+ years) & high risk tolerance."
                        : riskProfile.level === "Low"
                          ? "Ideal for conservative investors or short-term goals."
                          : "Suitable for medium-term outlook (3-5 years) seeking balance."}
                    </p>
                  </div>
                </div>
              </AccordionSection>
              <AccordionSection
                title="Fund Information & History"
                sectionId="history"
                Icon={Clock}
                expandedSection={expandedSection}
                toggleSection={toggleSection}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <InfoItem
                    label="Fund- House"
                    value={scheme.meta.fund_house}
                  />
                  <InfoItem
                    label="Category"
                    value={scheme.meta.scheme_category}
                  />
                  <InfoItem label="Type" value={scheme.meta.scheme_type} />
                  <InfoItem
                    label="Scheme Code"
                    value={scheme.meta.scheme_code}
                  />
                  <InfoItem
                    label="First Recorded NAV"
                    value={scheme.data?.[scheme.data.length - 1]?.nav}
                    date={scheme.data?.[scheme.data.length - 1]?.date}
                    isCurrency
                    isCompact
                  />
                  <InfoItem
                    label="Latest NAV"
                    value={latestNAV}
                    date={latestNAVDate}
                    isCurrency
                    isCompact
                  />
                  <InfoItem
                    label="ISIN (Growth)"
                    value={scheme.meta.isin_growth}
                    isCompact
                  />
                  <InfoItem
                    label="ISIN (Reinvest)"
                    value={scheme.meta.isin_div_reinvestment}
                    isCompact
                  />
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-600">
                    Total Growth Since Inception:
                  </p>
                  <p
                    className={`font-semibold text-lg ${inceptionGrowth > 0
                      ? "text-green-600"
                      : inceptionGrowth < 0
                        ? "text-red-600"
                        : "text-gray-800"
                      }`}
                  >
                    {growthFromInceptionFormatted}
                  </p>
                </div>
              </AccordionSection>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6 border border-gray-100">
              <h2 className="text-xl font-semibold text-gray-800 mb-5">
                Invest Now
              </h2>

              <div className="mb-4">
                <label
                  htmlFor="investAmount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Amount (₹)
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 pointer-events-none text-base">
                    ₹
                  </span>
                  <input
                    type="number"
                    id="investAmount"
                    value={investAmount}
                    onChange={(e) =>
                      setInvestAmount(
                        e.target.value ? parseFloat(e.target.value) : ""
                      )
                    }
                    min="100"
                    step="100"
                    className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium focus:outline-none text-right"
                    placeholder="Min 100"
                    aria-describedby="amount-error"
                  />
                </div>
                {investAmount !== "" && investAmount < 100 && (
                  <p id="amount-error" className="text-red-500 text-xs mt-1">
                    Minimum investment is ₹100
                  </p>
                )}
              </div>

              <div className="mb-5 bg-blue-50 p-3 rounded-md border border-blue-100 space-y-1 text-sm">
                <div className="flex justify-between items-center">
                  <p className="text-gray-600">Amount</p>
                  <p className="font-medium text-gray-800">
                    ₹{formatNumber(investAmount || 0)}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-gray-600">Current NAV</p>
                  <p className="font-medium text-gray-800">
                    ₹{formatNumber(latestNAV)}
                  </p>
                </div>
                <div className="flex justify-between items-center pt-1.5 border-t border-blue-100 mt-1.5">
                  <p className="text-gray-600">Est. Units</p>
                  <p className="font-semibold text-blue-800">
                    {estimatedUnits}
                  </p>
                </div>
              </div>

              <button
                onClick={handlePurchase}
                disabled={isInvestButtonDisabled}
                className={`w-full py-2.5 px-4 rounded-md font-semibold text-white transition-all duration-200 flex items-center justify-center text-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mb-3 ${isInvestButtonDisabled
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02]"
                  }`}
                title={
                  insufficientBalance
                    ? "Insufficient wallet balance"
                    : "Invest Now"
                }
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />{" "}
                    Processing...
                  </>
                ) : (
                  "Invest Now"
                )}
              </button>

              <button
                onClick={handleAddToWatchlist}
                disabled={isAddingToWatchlist}
                className={`w-full py-2.5 px-4 rounded-md font-semibold text-blue-600 border border-blue-600 bg-white hover:bg-blue-50 transition-all duration-200 flex items-center justify-center text-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isAddingToWatchlist ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                title="Add to Watchlist"
              >
                {isAddingToWatchlist ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" /> Adding...
                  </>
                ) : (
                  <>
                    <Bookmark size={18} className="mr-2" /> Add to Watchlist
                  </>
                )}
              </button>

              <div className="mt-5 text-xs text-gray-500 space-y-2">
                <p className="flex items-start">
                  <Info
                    size={14}
                    className="text-blue-400 mr-1.5 mt-0.5 flex-shrink-0"
                  />
                  NAV applicable as per SEBI cut-off times.
                </p>
                <p className="flex items-start">
                  <Info
                    size={14}
                    className="text-blue-400 mr-1.5 mt-0.5 flex-shrink-0"
                  />
                  Mutual funds are subject to market risks. Read documents
                  carefully.
                </p>
                <p className="flex items-start">
                  <Info size={14} className="text-blue-500 mr-1 mt-0.5" />
                  All mutual fund investments are subject to market risks. Read
                  all scheme related documents carefully.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// --- Reusable Helper Components ---
const AccordionSection = ({
  title,
  sectionId,
  Icon,
  expandedSection,
  toggleSection,
  children,
}) => {
  const isExpanded = expandedSection === sectionId;
  return (
    <div>
      <button
        className="flex items-center justify-between w-full px-6 py-4 text-left focus:outline-none hover:bg-gray-50 transition-colors duration-150"
        onClick={() => toggleSection(sectionId)}
        aria-expanded={isExpanded}
        aria-controls={`content-${sectionId}`}
      >
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          {Icon && (
            <Icon className="mr-3 text-blue-600" size={20} strokeWidth={2} />
          )}
          {title}
        </h2>
        {isExpanded ? (
          <ChevronUp size={20} className="text-gray-500" />
        ) : (
          <ChevronDown size={20} className="text-gray-500" />
        )}
      </button>
      {isExpanded && (
        <div
          id={`content-${sectionId}`}
          className="px-6 pb-5 pt-1 animate-fade-in"
        >
          {children}
        </div>
      )}
    </div>
  );
};

const InfoCard = ({
  label,
  value,
  date = null,
  datePrefix = "As of",
  suffix = "",
  isCurrency = false,
  isPercentage = false,
  icon: Icon = null,
  iconColor = "text-gray-400",
  description = null,
  isCompact = false,
}) => {
  const displayValue =
    value === null || value === undefined || isNaN(value)
      ? "N/A"
      : `${isCurrency ? "₹" : ""}${formatNumber(
        value,
        isPercentage ? 2 : isCurrency ? 2 : 0
      )}${suffix}`;
  const valueColor = isPercentage
    ? value > 0
      ? "text-green-600"
      : value < 0
        ? "text-red-600"
        : "text-gray-900"
    : "text-gray-900";

  return (
    <div
      className={`bg-gray-50 p-4 rounded-lg border border-gray-200 ${isCompact ? "py-3" : ""
        }`}
    >
      <p className={`text-sm text-gray-500 mb-1 ${isCompact ? "text-xs" : ""}`}>
        {label}
      </p>
      <div className="flex items-baseline gap-1">
        {Icon && (
          <Icon size={isCompact ? 14 : 16} className={`mr-1 ${iconColor}`} />
        )}
        <p
          className={`font-semibold text-gray-900 ${valueColor} ${isCompact ? "text-base" : "text-xl"
            }`}
        >
          {displayValue}
        </p>
      </div>
      {date && (
        <p
          className={`text-xs text-gray-500 mt-1 ${isCompact ? "mt-0.5" : ""}`}
        >
          {datePrefix}{" "}
          {new Date(date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </p>
      )}
      {description && (
        <p className={`text-xs text-gray-500 mt-1 ${Icon ? "ml-5" : ""}`}>
          {description}
        </p>
      )}
    </div>
  );
};

const InfoItem = ({
  label,
  value,
  date = null,
  isCurrency = false,
  isCompact = false,
}) => (
  <div
    className={` ${isCompact ? "py-1" : "bg-gray-50 p-3 rounded-lg border border-gray-100"
      }`}
  >
    <p className={`text-xs text-gray-500 ${isCompact ? "" : "mb-0.5"}`}>
      {label}
    </p>
    <p className={`font-medium text-sm text-gray-800 ${isCompact ? "" : ""}`}>
      {isCurrency && value !== null && value !== undefined ? "₹" : ""}
      {value !== null && value !== undefined
        ? isCurrency
          ? formatNumber(value)
          : value
        : "N/A"}
    </p>
    {date && (
      <p className={`text-xs text-gray-400 ${isCompact ? "mt-0" : "mt-0.5"}`}>
        {new Date(date).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </p>
    )}
  </div>
);

const Notification = ({ message, type }) => {
  const styles = {
    success: {
      bg: "bg-green-50",
      border: "border-green-400",
      text: "text-green-700",
      Icon: CheckCircle,
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-400",
      text: "text-red-700",
      Icon: XCircle,
    },
    warning: {
      bg: "bg-yellow-50",
      border: "border-yellow-300",
      text: "text-yellow-700",
      Icon: AlertTriangle,
    },
    info: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      Icon: Info,
    },
  };
  const currentStyle = styles[type] || styles.info;
  const IconComponent = currentStyle.Icon;

  return (
    <div
      className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg border-l-4 z-50 text-sm max-w-sm animate-fade-in ${currentStyle.bg} ${currentStyle.border} ${currentStyle.text}`}
      role="alert"
    >
      <div className="flex items-center">
        <IconComponent size={18} className="mr-2 flex-shrink-0" />
        <span className="flex-1">{message}</span>
      </div>
    </div>
  );
};

const ErrorDisplay = ({ message }) => (
  <div
    className="bg-red-50 border border-red-200 rounded-lg p-4 my-6 animate-fade-in"
    role="alert"
  >
    <div className="flex items-center">
      <AlertTriangle className="text-red-500 mr-3 flex-shrink-0" size={20} />
      <p className="text-red-700 text-sm font-medium">
        {message || "An unexpected error occurred."}
      </p>
    </div>
  </div>
);
